import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react'
import dayjs from 'dayjs'
import * as turf from '@turf/turf'
import { toast } from 'react-toastify'
import { getAllExercises } from '../services/request-form.service'
import { getFrequencyAssignments } from '../services/frequency-assignment.service'
import { createHeatmap } from '../services/rf-model.service'
import { IHeatmapRequest, IHeatmap, IHeatmapJob } from '../../shared/types/api/heatmap.type'
import { IFrequencyAssignment, IFrequencyAssignmentWithCoordinates, IExercise } from '../types/exercise.type'
import { AffectedCountries } from '../types/emitter.type'
import { sleep, toSpliced, dmsToDecimal, getAffectedCountries } from '../utils/functions'
import convertFrequencyToMHz from '../utils/convertFrequencyToMHz'
import prepareHeatmapParams from '../utils/prepareAreaRequestData'

interface IEmittersContext {
  frequencyAssignments: IFrequencyAssignmentWithCoordinates[]
  frequencyAssignmentsByCoord: IFrequencyAssignmentWithCoordinates[][]
  displayingFrequencyAssignmentIds: Set<string>
  setDisplayingFrequencyAssignmentIds: Dispatch<SetStateAction<Set<string>>>
  filters: Array<{ key: keyof IFrequencyAssignment; value: string | number }>
  startDate: string | null
  endDate: string | null
  frequencyRange: { min: number; max: number }
  areUploadsVisible: boolean
  exercisesToFilter: IExercise[]
  getExercises: () => void
  findEmitterBy: (
    findBy: Partial<IFrequencyAssignmentWithCoordinates>,
  ) => IFrequencyAssignmentWithCoordinates | undefined
  handleChangeDataBy: (
    findBy: Partial<IFrequencyAssignmentWithCoordinates>,
    newData: Partial<IFrequencyAssignmentWithCoordinates>,
  ) => void
  setHeatmap: (
    id_override: string,
    position: number,
    heatmap: IHeatmap | null,
    opts?: {
      heatmapsLoading?: boolean
      affectedCountries?: AffectedCountries
    },
  ) => void
  handleFilterChange: (index: number, key: keyof IFrequencyAssignment, value: string | number) => void
  handleFilterRemove: (index: number) => void
  handleDateRangeChange: (startDate: string | null, endDate: string | null) => void
  handleFrequencyRangechange: (value: { min: number; max: number }) => void
  clearFilters: () => void
  toggleUploadVisibility: () => void
  fetchOneHeatmap: (id_override: string, position: number, heatmapParams: IHeatmapRequest) => Promise<IHeatmap | null>
  fetchHeatmapForEmitter: (findBy: Partial<IFrequencyAssignmentWithCoordinates>) => Promise<void>
  selectEmitterData: (oldIdOverride: string, newIdOverride: string) => void
  showingRadii: Record<string, boolean>
  setShowRadius: (id_override: string, show: boolean) => void
  showingHeatmaps: Record<string, boolean>
  setShowHeatmap: (id_override: string, show: boolean) => void
}

const EmittersContext = createContext({} as IEmittersContext)

const groupByCoordinates = (frequencyAssignments: IFrequencyAssignment[]) => {
  const coordinateGroups: Map<string, IFrequencyAssignmentWithCoordinates[]> = new Map()

  for (const item of frequencyAssignments) {
    const latitude = dmsToDecimal(
      item.request_location_latitude_deg,
      item.request_location_latitude_min,
      item.request_location_latitude_sec,
      null,
    )
    const longitude = dmsToDecimal(
      item.request_location_longitude_deg,
      item.request_location_longitude_min,
      item.request_location_longitude_sec,
      null,
    )
    const key = `${latitude},${longitude}`

    if (!coordinateGroups.has(key)) {
      coordinateGroups.set(key, [])
    }
    // Add latitude and longitude to the item
    const updatedItem = {
      ...item,
      request_frequency_value: convertFrequencyToMHz(item.request_frequency_value),
      heatmaps: null,
      latitude: latitude,
      longitude: longitude,
      initialLat: latitude,
      initialLong: longitude,
      heatmapSimulation: null,
      heatmapParams: prepareHeatmapParams({
        alt: item.request_location_elevation || 1,
        txw: item.power,
        frq: convertFrequencyToMHz(item.request_frequency_value),
        lat: latitude,
        lon: longitude,
        rad: item.request_location_radius,
      }),
      exercise_end_date: dayjs(item.exercise_end_date),
      exercise_start_date: dayjs(item.exercise_start_date),
      receive_locations: item.receive_locations,
    }

    coordinateGroups.get(key)!.push(updatedItem)
  }

  // Convert the map to an array of arrays
  const newData = Array.from(coordinateGroups.values())
  newData.forEach((group) => {
    group[0].selected = true
  })
  return newData
}

const EmittersProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<Array<{ key: keyof IFrequencyAssignment; value: string | number }>>([])
  const [frequencyAssignmentsByCoord, setFrequencyAssignmentsByCoord] = useState<
    IFrequencyAssignmentWithCoordinates[][]
  >([])
  const [displayingFrequencyAssignmentIds, setDisplayingFrequencyAssignmentIds] = useState<Set<string>>(new Set())
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [frequencyRange, setFrequencyRange] = useState({ min: 0, max: 10000 })
  const [areUploadsVisible, setAreUploadsVisible] = useState(false)
  const [exercisesToFilter, setExercisesToFilter] = useState<IExercise[]>([])
  const frequencyAssignments = useMemo(() => frequencyAssignmentsByCoord.flat(), [frequencyAssignmentsByCoord])
  const [showingRadii, setShowingRadii] = useState<Record<string, boolean>>({})
  const [showingHeatmaps, setShowingHeatmaps] = useState<Record<string, boolean>>({})

  const controller = useRef<AbortController | null>(null)

  const getExercises = async () => {
    controller.current?.abort()
    const abortController = new AbortController()
    controller.current = abortController

    const response = await getFrequencyAssignments({
      startDate,
      endDate,
      frequencyMin: frequencyRange.min,
      frequencyMax: frequencyRange.max,
      filters,
    })

    // TODO: error handling
    if (response.data) setFrequencyAssignmentsByCoord(groupByCoordinates(response.data))
  }

  const getExercisesToFilter = async () => {
    const response = await getAllExercises()
    // TODO: error handling
    if (response.data) setExercisesToFilter(response.data)
  }

  const handleChangeDataBy = (
    findBy: Partial<IFrequencyAssignmentWithCoordinates>,
    newData: Partial<IFrequencyAssignmentWithCoordinates>,
  ) => {
    setFrequencyAssignmentsByCoord((prev) =>
      prev.map((group) =>
        group.map((item) => {
          const isMatch = Object.entries(findBy).every(
            ([key, value]) => item[key as keyof IFrequencyAssignmentWithCoordinates] === value,
          )
          return isMatch ? { ...item, ...newData } : item
        }),
      ),
    )
  }

  const arrayWithHeatmap = (
    heatmaps: Array<IHeatmap | null> | null,
    position: number,
    heatmap: IHeatmap | null,
  ): Array<IHeatmap | null> => {
    if (!heatmaps) heatmaps = Array(position).fill(null)

    while (heatmaps.length < position) {
      heatmaps = [...heatmaps, null]
    }

    return toSpliced(heatmaps, position, 1, heatmap)
  }

  const clearHeatmaps = (id_override: string) => {
    setFrequencyAssignmentsByCoord((prev) =>
      prev.map((group) =>
        group.map((item) =>
          item.id_override === id_override
            ? {
                ...item,
                heatmaps: [],
              }
            : item,
        ),
      ),
    )
  }

  const setHeatmap = (
    id_override: string,
    position: number,
    heatmap: IHeatmap | null,
    opts: {
      heatmapsLoading?: boolean
      affectedCountries?: AffectedCountries
    } = {},
  ) => {
    console.debug('setHeatmap', id_override, position, heatmap, opts)
    const {
      affectedCountries,
      // If we have a heatmap, it's not loading any more.
      // If not, either we asked for a new one and it's loading (the default),
      // or we got an error, so it's not loading any more.
      // For that last case, the caller should pass an explicit `heatmapsLoading: false`.
      heatmapsLoading = !heatmap,
    } = opts
    setFrequencyAssignmentsByCoord((prev) =>
      prev.map((group) =>
        group.map((item) =>
          item.id_override === id_override
            ? {
                ...item,
                heatmaps: arrayWithHeatmap(item.heatmaps, position, heatmap),
                affectedCountries: affectedCountries ?? item.affectedCountries,
                heatmapsLoading,
              }
            : item,
        ),
      ),
    )
  }

  /*
   * Start generating a heatmap, poll for completion, and return the result.
   */
  const fetchOneHeatmap = async (
    id_override: string,
    position: number,
    heatmapParams: IHeatmapRequest,
  ): Promise<IHeatmap> => {
    const reqParams: IHeatmapRequest = {
      id_override: id_override,
      position,
      ...heatmapParams,
    }

    let job: IHeatmapJob | null = null
    let heatmap: IHeatmap | null = null

    while (!heatmap) {
      const resp = await createHeatmap(reqParams)
      if (!resp.success || !resp.data) {
        throw new Error(resp.message)
      }

      ;({ job, heatmap } = resp.data)

      if (!heatmap) {
        if (!job || job.status === 'failed') {
          toast.error(job?.error || 'Job missing or failed')
          throw new Error(resp.message)
        }

        // We don't have a heatmap yet, but a job was created to draw one.
        // Keep trying until we get a result, passing the job id from now on.
        reqParams.job_id = job.id

        await sleep(1000)
      }
    }

    return heatmap
  }

  /*
   * Gets a heatmap for a frequency assignment (not a custom point).
   */
  const fetchHeatmapForEmitter = async (findBy: Partial<IFrequencyAssignmentWithCoordinates>) => {
    const emitter = frequencyAssignmentsByCoord
      .flat()
      .find((emitter) =>
        Object.entries(findBy).every(
          ([key, value]) => emitter[key as keyof IFrequencyAssignmentWithCoordinates] === value,
        ),
      )

    if (!emitter) return

    // If this is a mobile emitter, draw multiple heatmaps spaced out from start to end.
    // We draw one heatmap at both endpoints,
    // plus others spaced out in between, if there is room.
    // We always draw both endpoints, but otherwise we only draw heatmaps
    // if we can space them out at least 2r (where r is the requested radius).
    // TODO: Only consider it a mobile emitter if the locations broadcast (see f306 rules):
    const isMobileEmitter = emitter.receive_locations.length > 0
    if (isMobileEmitter) {
      // Store the main heatmap at index 0, and the others starting with 1:
      clearHeatmaps(emitter.id_override)
      // Generate all the heatmaps before we show any, so it looks like one image:
      // We always generate one at the main transmission site to start with.
      const heatmaps: Array<IHeatmap | null> = [await fetchOneHeatmap(emitter.id_override, 0, emitter.heatmapParams)]
      let prevLonlat = turf.point([emitter.heatmapParams.transmitter.lon, emitter.heatmapParams.transmitter.lat])
      let heatmapPosition = 1
      const radius = emitter.heatmapParams.output.rad
      for (let i = 0; i < emitter.receive_locations.length; i++) {
        const loc = emitter.receive_locations[i]
        // Skip it if any of the location parameters are missing:
        // (Sadly tsc doesn't understand this, so we need bangs below.)
        if (
          [
            loc.receive_request_location_latitude_deg,
            loc.receive_request_location_latitude_min,
            loc.receive_request_location_latitude_sec,
            loc.receive_request_location_longitude_deg,
            loc.receive_request_location_longitude_min,
            loc.receive_request_location_longitude_sec,
          ].some((x) => x == null)
        )
          continue

        const lat = dmsToDecimal(
          loc.receive_request_location_latitude_deg!,
          loc.receive_request_location_latitude_min!,
          loc.receive_request_location_latitude_sec!,
          null,
        )
        const lon = dmsToDecimal(
          loc.receive_request_location_longitude_deg!,
          loc.receive_request_location_longitude_min!,
          loc.receive_request_location_longitude_sec!,
          null,
        )

        // const latlon = L.latLng(lat, lon)
        const lonlat = turf.point([lon, lat])
        // const distance = prevLonlat.distanceTo(lonlat)  // in meters
        const distance = turf.distance(prevLonlat, lonlat) // in kilometers
        // Cap the points at 20:
        const middlePoints = Math.min(Math.floor(distance / (2 * radius)) - 1, 20)
        const spread = distance / middlePoints
        const lonlats = []
        console.debug(`distance = ${distance} meters, radius = ${radius} km`)
        console.debug(`middlePoints = ${middlePoints} spread = ${spread} km`)
        const bearing = turf.bearing(prevLonlat, lonlat)
        let curLonlat = prevLonlat
        for (let j = 0; j < middlePoints; j++) {
          // Move spread along the path:
          curLonlat = turf.destination(curLonlat, spread, bearing)
          lonlats.push(curLonlat)
        }
        lonlats.push(lonlat)

        for (const site of lonlats) {
          const locParams = {
            ...emitter.heatmapParams,
            transmitter: {
              ...emitter.heatmapParams.transmitter,
              lat: site.geometry.coordinates[1],
              lon: site.geometry.coordinates[0],
            },
          }
          // N.B. This still generates heatmaps one-by-one.
          // If we think BrightRF can handle it, maybe we should start them all at once,
          // then wait until we have all the results?
          heatmaps.push(null) // make room first
          heatmaps[heatmapPosition] = await fetchOneHeatmap(emitter.id_override, heatmapPosition, locParams)
          console.debug(
            `Got a heatmap for ${heatmapPosition}: ${site.geometry.coordinates[1]} ${site.geometry.coordinates[0]}`,
          )
          heatmapPosition++
        }
      }

      const affectedCountries = getAffectedCountries(heatmaps[0])
      for (let i = 0; i < heatmaps.length; i++) {
        setHeatmap(emitter.id_override, i, heatmaps[i], { affectedCountries })
      }
    } else {
      setHeatmap(emitter.id_override, 0, null)

      const heatmap = await fetchOneHeatmap(emitter.id_override, 0, emitter.heatmapParams)

      const affectedCountries = getAffectedCountries(heatmap)

      setHeatmap(emitter.id_override, 0, heatmap, { affectedCountries })
    }
  }

  const findEmitterBy = (
    findBy: Partial<IFrequencyAssignmentWithCoordinates>,
  ): IFrequencyAssignmentWithCoordinates | undefined => {
    for (const group of frequencyAssignmentsByCoord) {
      for (const item of group) {
        const isMatch = Object.entries(findBy).every(
          ([key, value]) => item[key as keyof IFrequencyAssignmentWithCoordinates] === value,
        )
        if (isMatch) {
          return item
        }
      }
    }
    return undefined
  }

  const toggleUploadVisibility = () => setAreUploadsVisible((prev) => !prev)
  // Filters mutation
  const handleFilterChange = (index: number, key: keyof IFrequencyAssignment, value: string | number) => {
    const newFilters = [...filters]
    if (index >= newFilters.length) {
      newFilters.push({ key, value })
    } else {
      newFilters[index] = { key, value }
    }
    setFilters(newFilters)
  }

  const handleFilterRemove = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index)
    setFilters(newFilters)
  }

  const handleDateRangeChange = (startDate: string | null, endDate: string | null) => {
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const clearFilters = () => {
    setFilters([])
    setStartDate(null)
    setEndDate(null)
    setFrequencyRange({ min: 0, max: 10000 })
    getExercises()
  }

  const selectEmitterData = (oldIdOverride: string, newIdOverride: string) => {
    setFrequencyAssignmentsByCoord((prev) =>
      prev.map((group) =>
        group.map((el) => {
          const newEl = { ...el }

          if (newEl.id_override === oldIdOverride) {
            newEl.selected = false
          }
          if (newEl.id_override === newIdOverride) {
            newEl.selected = true
          }

          return newEl
        }),
      ),
    )
  }

  const setShowRadius = (id_override: string, show: boolean) => {
    setShowingRadii((prev) => ({
      ...prev,
      [id_override]: show,
    }))
  }

  const setShowHeatmap = (id_override: string, show: boolean) => {
    setShowingHeatmaps((prev) => ({
      ...prev,
      [id_override]: show,
    }))
  }

  useEffect(() => {
    getExercises()
    getExercisesToFilter()
  }, [])

  return (
    <EmittersContext.Provider
      value={{
        frequencyAssignments,
        frequencyAssignmentsByCoord,
        displayingFrequencyAssignmentIds,
        setDisplayingFrequencyAssignmentIds,
        filters,
        startDate,
        endDate,
        frequencyRange,
        areUploadsVisible,
        exercisesToFilter,
        handleChangeDataBy,
        setHeatmap,
        handleFilterChange,
        handleFilterRemove,
        handleDateRangeChange,
        findEmitterBy,
        handleFrequencyRangechange: setFrequencyRange,
        getExercises,
        clearFilters,
        toggleUploadVisibility,
        fetchOneHeatmap,
        fetchHeatmapForEmitter,
        selectEmitterData,
        showingRadii,
        setShowRadius,
        showingHeatmaps,
        setShowHeatmap,
      }}
    >
      {children}
    </EmittersContext.Provider>
  )
}

export const useEmitters = () => {
  const context = useContext(EmittersContext)
  if (context === undefined) {
    throw new Error('useEmitters must be used within an EmittersProvider')
  }
  return context
}

export default EmittersProvider
